/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import {
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  Grid,
  MenuItem,
  Menu,
  Box,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Mail as MailIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Inbox as InboxIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  LockOutlined,
  LockOpen,
  FileDownload,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { apiUrl } from '../../utilities/apiUrl';
import {
  SingleMessageModal,
  MultiMessageModal,
  QueryContainer,
  OutlinedButton,
  ContainedButton,
  AddEmpModal,
  SelectDropdown,
  GeneralTable,
  CustomTableCell,
  ActionTableCell,
  CustomTableRow,
  Search,
  EditEmpModal,
  DeleteEmpModal,
  SentMessagesModal,
  InspectEmpModal,
  LockEmpModal,
  UnlockEmpModal,
} from '../index';
import {
  sortByFirstName,
  sortByLastName,
  sortByUsername,
  // sortByCountry,
} from '../../utilities/sortBy';
import { useOutletContext } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { pdf } from '@react-pdf/renderer';
import { EmployeeDataPDF } from './EmployeeDataPDF';
export const EmployeeAccounts = (props) => {
  const [empId] = useOutletContext();
  const employeeData = props.data;
  const [entryPerPage, setEntryPerPage] = useState(10);
  const [employeeList, setEmployeeList] = useState([]);
  const [slice, setSlice] = useState([0, entryPerPage]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(
    Math.floor(employeeList.length / entryPerPage)
  );
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [inspectModalOpen, setInspectModalOpen] = useState(false);
  const [multiMessageModalOpen, setMultiMessageModalOpen] = useState(false);
  const [inboxModalOpen, setInboxModalOpen] = useState(false);
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [lastname, setLastname] = useState('');
  const [firstname, setFirstname] = useState('');
  const [username, setUsername] = useState('');
  const [middlename, setMiddlename] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('REGULAR_USER');
  const [anchorEl, setAnchorEl] = useState(null);
  const openAction = Boolean(anchorEl);
  const [deleteName, setDeleteName] = useState('');
  const [lockName, setLockName] = useState('');
  const [unlockName, setUnlockName] = useState('');
  const [selectedEmp, setSelectedEmp] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [order, setOrder] = useState('');
  const [noRecord, setNoRecord] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientNames, setRecipientNames] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState('');
  const [lockFilter, setLockFilter] = useState('ALL');

  //Reset to page 1 when changing the number of entries
  useEffect(() => {
    setCurrentPage(1);
    setPageCount(Math.floor(employeeList.length / entryPerPage));
    setSlice([0, entryPerPage]);
  }, [entryPerPage]);

  // GET EMPLOYEE ROLE
  useEffect(() => {
    fetch(apiUrl('/employee/' + empId), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setIsSuperAdmin(data.data.role === 'SUPER_ADMIN' ? true : false);
      });
  }, [empId]);
  // LOAD DATA AS SOON AS PAGE RENDERS
  useEffect(() => {
    if (searchValue === '' && sortBy === '' && order === '') {
      setEmployeeList(
        employeeData.sort((a, b) => {
          const empDataSort = a.lastname.localeCompare(b.lastname);
          return empDataSort !== 0
            ? empDataSort
            : a.firstname.localeCompare(b.firstname);
        })
      );
    }
    setPageCount(Math.floor(employeeList.length / entryPerPage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeData]);
  ``;
  // PAGE PAGINATION HANDLER
  const handlePageChange = (e, page) => {
    setCurrentPage(page);
    let currPage = page - 1;
    if (page === 1) {
      setSlice([0, entryPerPage]);
    } else {
      setSlice([currPage * entryPerPage, page * entryPerPage]);
    }
  };
  useEffect(() => {
    let updatedList = [...employeeData];
    // Lock Filter
    if (lockFilter === 'UNLOCK_ONLY') {
      updatedList = updatedList.filter((emp) => !emp.islocked);
    } else if (lockFilter === 'LOCK_ONLY') {
      updatedList = updatedList.filter((emp) => emp.islocked);
    }
    // Search Filter
    if (searchValue) {
      const searchUpper = searchValue.toUpperCase();
      updatedList = updatedList.filter(
        (emp) =>
          emp.firstname.toUpperCase().includes(searchUpper) ||
          emp.lastname.toUpperCase().includes(searchUpper) ||
          `${emp.firstname} ${emp.lastname}`
            .toUpperCase()
            .includes(searchUpper) ||
          `${emp.lastname} ${emp.firstname}`.toUpperCase().includes(searchUpper)
      );
    }
    // Sorting
    if (sortBy === 'firstname') {
      updatedList = sortByFirstName(updatedList);
    } else if (sortBy === 'lastname') {
      updatedList = sortByLastName(updatedList);
    } else if (sortBy === 'username') {
      updatedList = sortByUsername(updatedList);
    }
    // Order
    if (order === 'desc') {
      updatedList = updatedList.reverse();
    }

    setEmployeeList(updatedList);
    // Update pagination
    setPageCount(Math.floor(updatedList.length / entryPerPage));

    setCurrentPage(1);
    setSlice([0, entryPerPage]);
    // Update noRecord state
    setNoRecord(updatedList.length === 0);
  }, [employeeData, lockFilter, searchValue, sortBy, order]);
  // SEARCH HANDLER
  const handleSearch = () => {
    // Just reset pagination; filtering happens in useEffect
    setCurrentPage(1);
    setSlice([0, entryPerPage]);
  };
  // SORTING HANDLER
  const handleSortChange = (event) => setSortBy(event.target.value);
  // LOCK FILTER HANDLER
  const handleLockChange = (event) => {
    setLockFilter(event.target.value);
  };
  // ORDER HANDLER
  const handleOrderChange = (event) => setOrder(event.target.value);
  // RESET QUERY HANDLER
  const handleReset = () => {
    setLockFilter('ALL');
    setSortBy('');
    setOrder('');
    setSearchValue('');
    setNoRecord(false);
    setCurrentPage(1);
    setSlice([0, entryPerPage]);
  };
  // DELETE CONFIRMATION MODAL HANDLER
  const handleDeleteModalOpen = () => {
    const currentEmp = employeeList[selectedIndex];
    setDeleteModalOpen(true);
    setSelectedEmp(currentEmp._id);
    setDeleteName(`${currentEmp.firstname} ${currentEmp.lastname}`);
  };
  // LOCK CONFIRMATION MODAL HANDLER
  const handleLockModalOpen = () => {
    const currentEmp = employeeList[selectedIndex];
    setLockModalOpen(true);
    setSelectedEmp(currentEmp._id);
    setLockName(`${currentEmp.firstname} ${currentEmp.lastname}`);
  };
  // UNLOCK CONFIRMATION MODAL HANDLER
  const handleUnlockModalOpen = () => {
    const currentEmp = employeeList[selectedIndex];
    setUnlockModalOpen(true);
    setSelectedEmp(currentEmp._id);
    setUnlockName(`${currentEmp.firstname} ${currentEmp.lastname}`);
  };
  // EDIT MODAL HANDLER
  const handleEditModalOpen = () => {
    const currentEmp = employeeList[selectedIndex];
    setEditModalOpen(true);
    setSelectedEmp(currentEmp._id);
    setLastname(currentEmp.lastname);
    setFirstname(currentEmp.firstname);
    setUsername(currentEmp.username);
    setRole(currentEmp.role);
  };
  const handleInspectModalOpen = () => {
    const currentEmp = employeeList[selectedIndex];
    setInspectModalOpen(true);
    setSelectedEmp(currentEmp._id);
    setLastname(currentEmp.lastname);
    setFirstname(currentEmp.firstname);
    setUsername(currentEmp.username);
    setMiddlename(currentEmp.middlename);
    setEmail(currentEmp.email);
    setRole(currentEmp.role);
  };
  // SINGLE MESSAGE MODAL HANDLER
  const handleMessageModalOpen = () => {
    const currentEmp = employeeList[selectedIndex];
    setMessageModalOpen(true);
    setSelectedEmp(currentEmp._id);
    setLastname(currentEmp.lastname);
    setFirstname(currentEmp.firstname);
  };
  // BULK MESSAGE MODAL HANDLER
  const handleMultiMsgModalOpen = () => {
    setMultiMessageModalOpen(true);
    employeeData.map((emp) =>
      setRecipients((prevEmployee) => [
        ...prevEmployee,
        `${emp.firstname} ${emp.lastname}`,
      ])
    );
  };
  // SENT MESSAGES MODAL HANDLER
  const handleInboxModalOpen = () => {
    setInboxModalOpen(true);
    fetch(apiUrl('/employee'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setRecipientNames(
          data.data.map((emp) => ({
            empid: emp._id,
            name: `${emp.firstname} ${emp.lastname}`,
          }))
        );
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleDownloadEmployeePDF = async (empId) => {
    try {
      // Helper to fetch data from an endpoint
      const fetchData = async (endpoint) => {
        const res = await fetch(apiUrl(endpoint), {
          method: 'GET',
          credentials: 'include',
        });
        const result = await res.json();
        if (result.success) return result.data;
        return null;
      };

      // Fetch all necessary data in parallel
      const [
        employee,
        empAddress,
        empWorkExp,
        empCivicWork,
        empTraining,
        empRelations,
        empEducation,
        empCivService,
        empHistory,
      ] = await Promise.all([
        fetchData(`/employee/${empId}`),
        fetchData(`/eis/empaddress/${empId}`),
        fetchData(`/eis/empworkexp/${empId}`),
        fetchData(`/eis/empcivicwork/${empId}`),
        fetchData(`/eis/emptraining/${empId}`),
        fetchData(`/eis/emprelations/${empId}`),
        fetchData(`/eis/empeducation/${empId}`),
        fetchData(`/eis/empcivservice/${empId}`),
        fetchData(`/eis/emphistory/${empId}`),
      ]);

      // Prepare PDF document
      const pdfDoc = (
        <EmployeeDataPDF
          employee={employee}
          empaddress={empAddress}
          emprelations={empRelations}
          empeducation={empEducation}
          empcivservice={empCivService}
          empworkexp={empWorkExp}
          empcivicwork={empCivicWork}
          emptraining={empTraining}
          emphistory={empHistory}
        />
      );

      // Generate PDF blob
      const blob = await pdf(pdfDoc).toBlob();

      // Trigger download
      saveAs(
        blob,
        `${employee?.firstname}_${employee?.lastname}_${employee?._id}.pdf`
      );
    } catch (err) {
      console.error('Error generating PDF:', err);
    }
  };

  return (
    <>
      <QueryContainer>
        <Grid item>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <SelectDropdown
                sx={{ minWidth: 200 }}
                size="small"
                label="Sort by"
                value={sortBy}
                onChange={handleSortChange}
              >
                <MenuItem value={'lastname'}>Last Name</MenuItem>
                <MenuItem value={'firstname'}>First Name</MenuItem>
                <MenuItem value={'username'}>Username</MenuItem>
                {/* <MenuItem value={"country"}>Country</MenuItem> */}
              </SelectDropdown>
            </Grid>
            <Grid item>
              <SelectDropdown
                sx={{ minWidth: 200 }}
                size="small"
                label="Order"
                value={order}
                onChange={handleOrderChange}
              >
                <MenuItem value={'asc'}>Ascending</MenuItem>
                <MenuItem value={'desc'}>Descending</MenuItem>
              </SelectDropdown>
            </Grid>
            <Grid item>
              <Search
                searchValue={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                handleSearch={handleSearch}
              />
            </Grid>
            <Grid item>
              <OutlinedButton btnTitle="Reset Query" onClick={handleReset} />
            </Grid>
            <Grid item>
              <SelectDropdown
                sx={{ minWidth: 220 }}
                size="small"
                label="Lock Filter"
                value={lockFilter}
                onChange={handleLockChange}
              >
                <MenuItem value={'ALL'}>All Employees</MenuItem>
                <MenuItem value={'UNLOCK_ONLY'}>Unlocked Employees</MenuItem>
                <MenuItem value={'LOCK_ONLY'}>Locked Employees</MenuItem>
              </SelectDropdown>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container spacing={2} alignItems="center">
            {/* <Grid item> [EPSv5_Dashboard-SuperAdminButtons]
              <ContainedButton
                onClick={handleInboxModalOpen}
                btnTitle="Message Inbox"
                startIcon={<InboxIcon />}
                style={{ px: 1, width: 160 }}
              />
            </Grid>
            <Grid item>
              <ContainedButton
                onClick={() => handleMultiMsgModalOpen()}
                btnTitle="Bulk Message"
                startIcon={<MailIcon />}
                style={{ px: 1, width: 160 }}
              />
            </Grid> */}
            <Grid item>
              {isSuperAdmin && (
                <ContainedButton
                  onClick={() => setAddModalOpen(true)}
                  btnTitle="Add Employee"
                  startIcon={<AddIcon />}
                  style={{ px: 1, width: 160 }}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </QueryContainer>
      <GeneralTable
        isDataLoading={props.isLoading}
        noRecord={noRecord}
        sx={
          noRecord
            ? { display: 'none' }
            : {
                borderRadius: '5px',
                boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
                mb: 3.6,
                display: 'block',
              }
        }
        size="small"
        length={employeeList.length}
        entryPerPage={entryPerPage}
        page={currentPage}
        pageCount={pageCount}
        handlePageChange={handlePageChange}
        entriesPerPageComponent={
          <SelectDropdown
            sx={{ minWidth: 80 }}
            size="small"
            label="Entries"
            value={entryPerPage}
            onChange={(e) => setEntryPerPage(e.target.value)}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </SelectDropdown>
        }
      >
        <TableHead>
          <TableRow>
            <CustomTableCell value="Last Name" />
            <CustomTableCell align="left" value="First Name" />
            <CustomTableCell align="left" value="Username" />
            <CustomTableCell align="left" value="Email" />
            <CustomTableCell align="left" value="Active" />
            <CustomTableCell align="center" value="Locked" />
            {isSuperAdmin && <CustomTableCell align="center" value="Actions" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {employeeList.slice(slice[0], slice[1]).map((emp, index) => (
            <CustomTableRow key={emp._id}>
              <CustomTableCell scope="row" value={emp.lastname} />
              <CustomTableCell scope="row" value={emp.firstname} />
              <CustomTableCell scope="row" value={emp.username} />
              <CustomTableCell scope="row" value={emp.email} />
              <CustomTableCell
                scope="row"
                value={emp.active ? 'Active' : 'Inactive'}
              />
              <CustomTableCell
                scope="row"
                value={
                  emp.islocked ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <LockOutlined />
                    </Box>
                  ) : null
                }
              />
              {isSuperAdmin && (
                <ActionTableCell>
                  <IconButton
                    id={index}
                    size="small"
                    title="more"
                    aria-controls={openAction ? 'basic-menu' : undefined}
                    aria-expanded={openAction ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={(event) => {
                      setAnchorEl(event.currentTarget);
                      setSelectedIndex(
                        (index + entryPerPage) * (currentPage - 1) -
                          (currentPage - 2) * index
                      );
                    }}
                  >
                    <MoreVertIcon fontSize="inherit" />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={openAction}
                    onClose={() => setAnchorEl(null)}
                    sx={{ pt: '0px !important' }}
                  >
                    {/* For the HRIS update, directly editing an employee is not allowed */}
                    {/* <MenuItem
                      onClick={() => handleEditModalOpen(true)}
                      sx={{ pl: 0.8 }}
                      divider
                    >
                      <EditIcon sx={{ mx: 1 }} fontSize="small" />
                      Edit
                    </MenuItem> */}
                    {/* <MenuItem [EPSv5_Manage-MessageEmployee]
                    onClick={handleMessageModalOpen}
                    sx={{ pl: 0.8 }}
                    divider
                  >
                    <MailIcon sx={{ mx: 1 }} fontSize="small" />
                    Message{" "}
                  </MenuItem> */}
                    <MenuItem onClick={handleInspectModalOpen} sx={{ pl: 0.8 }}>
                      <SearchIcon sx={{ mx: 1 }} fontSize="small" />
                      Inspect{' '}
                    </MenuItem>
                    <MenuItem
                      divider
                      sx={{ pl: 0.8 }}
                      onClick={() =>
                        handleDownloadEmployeePDF(
                          employeeList[selectedIndex]?._id
                        )
                      }
                    >
                      <FileDownload sx={{ mx: 1 }} fontSize="small" />
                      Download
                    </MenuItem>
                    {employeeList[selectedIndex]?.islocked ? (
                      <>
                        <MenuItem
                          onClick={handleUnlockModalOpen}
                          sx={{ pl: 0.8 }}
                        >
                          <LockOpen sx={{ mx: 1 }} fontSize="small" />
                          Unlock
                        </MenuItem>
                        <MenuItem
                          onClick={handleDeleteModalOpen}
                          sx={{ pl: 0.8 }}
                        >
                          <DeleteIcon sx={{ mx: 1 }} fontSize="small" />
                          Delete
                        </MenuItem>
                      </>
                    ) : (
                      <MenuItem onClick={handleLockModalOpen} sx={{ pl: 0.8 }}>
                        <LockOutlined sx={{ mx: 1 }} fontSize="small" />
                        Lock
                      </MenuItem>
                    )}
                  </Menu>
                </ActionTableCell>
              )}
            </CustomTableRow>
          ))}
        </TableBody>
      </GeneralTable>
      <InspectEmpModal
        open={inspectModalOpen}
        onClose={() => setInspectModalOpen(false)}
        firstname={firstname}
        lastname={lastname}
        middlename={middlename}
        username={username}
        email={email}
        empid={employeeList[selectedIndex]?._id}
      />
      <LockEmpModal
        open={lockModalOpen}
        onClose={() => setLockModalOpen(false)}
        lockName={lockName}
        resetIndex={() => {
          setSelectedIndex(null);
          setAnchorEl(null);
        }}
        selectedEmp={selectedEmp}
      />
      <UnlockEmpModal
        open={unlockModalOpen}
        onClose={() => setUnlockModalOpen(false)}
        unlockName={unlockName}
        resetIndex={() => {
          setSelectedIndex(null);
          setAnchorEl(null);
        }}
        selectedEmp={selectedEmp}
      />
      <AddEmpModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        secondaryClick={() => setAddModalOpen(false)}
      />
      <EditEmpModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        selectedEmp={selectedEmp}
        firstname={firstname}
        lastname={lastname}
        username={username}
        role={role}
      />
      <SingleMessageModal
        open={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        primaryClickHandler={null} //no send function yet
        secondaryClickHandler={() => setMessageModalOpen(false)}
        recipient={`${firstname} ${lastname}`}
      ></SingleMessageModal>
      <MultiMessageModal
        open={multiMessageModalOpen}
        onClose={() => setMultiMessageModalOpen(false)}
        secondaryClickHandler={() => setMultiMessageModalOpen(false)}
        options={recipients}
        employeeList={employeeData}
      ></MultiMessageModal>
      <DeleteEmpModal
        handleDownloadEmployeePDF={handleDownloadEmployeePDF}
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        deleteName={deleteName}
        resetIndex={() => {
          setSelectedIndex(null);
          setAnchorEl(null);
        }}
        selectedEmp={selectedEmp}
      />
      <SentMessagesModal
        open={inboxModalOpen}
        onClose={() => setInboxModalOpen(false)}
        recipientList={recipientNames}
      />
    </>
  );
};
